/**
 * Customer-journey configuration for the homepage chips.
 *
 * 2026-02-24: We replaced the flat 11-chip grid with 5 buyer-intent
 * journeys + an optional 6th returning-user chip. Every original chip
 * still exists as a SUB-CHIP inside its parent journey and opens the
 * SAME BuildTypePopup with the SAME matchers / resources / explore CTAs.
 *
 * Zero content was deleted. The journey layer is purely an outer
 * navigation wrapper that adds buyer-intent clarity, an SEO landing
 * page, and (for Journey 5) external state-specific dispute resources.
 *
 *   Journey ─ contains ─→ Sub-chip ─ opens ─→ BuildTypePopup
 *                                                ├─ matchers (Find a X)
 *                                                ├─ resources (deep links)
 *                                                └─ explore CTA
 *
 * Editing rules:
 *   • Adding a new journey = add to JOURNEYS[]
 *   • Adding a new sub-chip = add to a journey's subChips[]
 *     The `href` MUST match a key in buildTypePopupConfig.js so the
 *     popup content is preserved automatically.
 */

export const JOURNEYS = [
  // ── 1 · Build a Home ───────────────────────────────────────────────
  {
    slug: 'build-a-home',
    name: 'Build a Home',
    subtitle: 'From picking land to picking up the keys.',
    iconSrc: '/images/icons/new-homes.avif',
    seoH1: 'Building a home in Australia',
    seoSummary:
      'Whether you’re still hunting for the right block or you already have land and a design, BuildPilot helps you make the right call at every stage of a new-home build.',
    askQuestion: 'Where are you in your build journey?',
    subChips: [
      {
        label: 'Choosing a block of land',
        href: '/choosing-land',
        iconSrc: '/images/icons/choosing-land.avif',
        when: 'Haven’t picked land yet',
      },
      {
        label: 'New homes',
        href: '/building-a-new-home',
        iconSrc: '/images/icons/new-homes.avif',
        when: 'Have land · need a builder',
      },
      {
        label: 'Home design',
        href: '/home-design',
        iconSrc: '/images/icons/home-design.avif',
        when: 'Researching floor plans',
      },
      {
        label: 'Changing builders',
        href: '/changing-builders',
        iconSrc: '/images/icons/changing-builders.avif',
        when: 'Already mid-build',
      },
    ],
  },

  // ── 2 · Renovate & Extend ──────────────────────────────────────────
  {
    slug: 'renovate-and-extend',
    name: 'Renovate & Extend',
    subtitle: 'Refresh, extend or restyle a home you already own.',
    iconSrc: '/images/icons/renovations.avif',
    seoH1: 'Renovating or extending an Australian home',
    seoSummary:
      'A great renovation balances design, structure and budget. BuildPilot helps you choose the right specialist, understand what’s structural vs cosmetic, and avoid the contracts and surprises that derail most renos.',
    askQuestion: 'What kind of change are you planning?',
    subChips: [
      {
        label: 'Renovations',
        href: '/renovations',
        iconSrc: '/images/icons/renovations.avif',
        when: 'Updating an existing home',
      },
      {
        label: 'Interior design',
        href: '/interior-design',
        iconSrc: '/images/icons/interior-design.avif',
        when: 'Need style + space planning',
      },
    ],
  },

  // ── 3 · Improve My Home ────────────────────────────────────────────
  {
    slug: 'improve-my-home',
    name: 'Improve My Home',
    subtitle: 'Outdoor, garden and lifestyle additions.',
    iconSrc: '/images/icons/decking.avif',
    seoH1: 'Home improvements: pools, pergolas, decks and more',
    seoSummary:
      'One project at a time or bundle them - BuildPilot connects you to either a single full-service builder who can deliver everything together, or to specialist tradies for one-trade jobs.',
    askQuestion: 'What outdoor or improvement project are you planning?',
    subChips: [
      {
        label: 'Verandahs or pergolas',
        href: '/verandahs-pergolas',
        iconSrc: '/images/icons/verandahs-pergolas.avif',
        when: 'Adding outdoor coverage',
      },
      {
        label: 'Landscaping',
        href: '/landscaping',
        iconSrc: '/images/icons/landscaping.avif',
        when: 'Garden, paving, irrigation',
      },
      {
        label: 'Decking',
        href: '/decking',
        iconSrc: '/images/icons/decking.avif',
        when: 'Timber or composite decking',
      },
      {
        label: 'Sheds or garages',
        href: '/sheds-garages',
        iconSrc: '/images/icons/sheds-garages.avif',
        when: 'Storage, workshop or carport',
      },
      {
        label: 'Swimming pools',
        href: '/swimming-pools',
        iconSrc: '/images/icons/swimming-pools.avif',
        when: 'Concrete or fibreglass pool',
      },
    ],
  },

  // ── 4 · Invest & Develop ───────────────────────────────────────────
  {
    slug: 'invest-and-develop',
    name: 'Invest & Develop',
    subtitle:
      'Buy turnkey, subdivide, knock down & rebuild, or develop.',
    iconSrc: '/images/icons/property-development.avif',
    seoH1: 'Property investment and development in Australia',
    seoSummary:
      'Investors don’t always want to develop - sometimes you just want a yield-ready home & land package. BuildPilot covers both ends of the spectrum, from turnkey investments to multi-lot subdivisions.',
    askQuestion: 'What’s your investment angle?',
    subChips: [
      {
        label: 'Home & Land Package',
        href: '/house-and-land-adelaide',
        iconSrc: '/images/icons/new-homes.avif',
        when: 'Turnkey investment property',
      },
      {
        label: 'Invest or develop',
        href: '/property-development',
        iconSrc: '/images/icons/property-development.avif',
        when: 'Subdivide, KDR, flip or hold',
      },
    ],
  },

  // ── 5 · My Build Needs Help ────────────────────────────────────────
  {
    slug: 'my-build-needs-help',
    name: 'My Build Needs Help',
    subtitle: 'Something’s gone wrong with your current build.',
    iconSrc: '/images/icons/changing-builders.avif',
    seoH1: 'Help with a build that’s gone wrong',
    seoSummary:
      'If your build is stalling, your builder has gone quiet, or you’ve found a defect - BuildPilot routes you to the right state authority and gives you the plain-English playbook for what to do next.',
    askQuestion: 'What’s gone wrong?',
    // Sub-chips for J5 route to state-specific external resource pages
    // (built programmatically at /help/building-disputes/:state/:issue).
    // The first 4 also surface the existing Changing-Builders matcher
    // because switching builders is often the next step.
    subChips: [
      {
        label: 'Builder’s gone silent',
        href: '/help/building-disputes?issue=silent-builder',
        iconSrc: '/images/icons/changing-builders.avif',
        when: 'No calls, no progress',
        external: true,
      },
      {
        label: 'Defect or quality issue',
        href: '/help/building-disputes?issue=defect-or-quality',
        iconSrc: '/images/icons/renovations.avif',
        when: 'Something’s been built wrong',
        external: true,
      },
      {
        label: 'Long delays',
        href: '/help/building-disputes?issue=long-delays',
        iconSrc: '/images/icons/changing-builders.avif',
        when: 'Months behind schedule',
        external: true,
      },
      {
        label: 'Scope changes or variations',
        href: '/help/building-disputes?issue=scope-changes',
        iconSrc: '/images/icons/home-design.avif',
        when: 'Costs you didn’t agree to',
        external: true,
      },
      {
        label: 'Changing builders',
        href: '/changing-builders',
        iconSrc: '/images/icons/changing-builders.avif',
        when: 'Want to switch builders',
      },
    ],
  },

  // ── 6 · Finishing & Moving In ──────────────────────────────────────
  // The "after the build / just finished" stage. Its popup is designed
  // to grow: building inspections, what-to-look-out-for guidance, and
  // (once products are amalgamated) relevant homeware product picks.
  {
    slug: 'finishing-and-moving-in',
    name: 'Finishing or Moving In',
    subtitle: 'Wrapping up, settling in and making the home yours.',
    iconSrc: '/images/icons/love.webp',
    seoH1: 'Finishing your project and moving into your home',
    seoSummary:
      'Whether you are wrapping up a build, a renovation or just settling into a home - this is the stage where you check the work, sort out anything that needs fixing, and make the place yours. BuildPilot helps you get it right and plan whatever comes next.',
    askQuestion: 'Where are you up to?',
    subChips: [
      {
        label: 'Building inspection',
        href: '/building-inspectors',
        iconSrc: '/images/icons/home-design.avif',
        when: 'Independent check before you sign off',
      },
      {
        label: 'Final checks & warranty',
        href: '/warranty-and-after-handover-adelaide',
        iconSrc: '/images/icons/new-homes.avif',
        when: 'What to look out for once it is done',
      },
      {
        label: 'Renovate or extend',
        href: '/renovations',
        iconSrc: '/images/icons/renovations.avif',
        when: 'Update a home you already own',
      },
      {
        label: 'Add a granny flat',
        href: '/granny-flat-guide',
        iconSrc: '/images/icons/new-homes.avif',
        when: 'Extra space or rental income',
      },
      {
        label: 'Outdoor & landscaping',
        href: '/landscaping',
        iconSrc: '/images/icons/decking.avif',
        when: 'Finish off the yard',
      },
    ],
  },
];

// ── 6th chip - returning-user shortcut ───────────────────────────────
// Not a journey - just a direct link to the /workspace dashboard for
// users mid-build. Sits visually separate from the 5 journey chips.
export const RETURNING_USER_CHIP = {
  slug: 'im-already-building',
  name: 'I’m Already Building',
  subtitle: 'Resume your workspace, tracker and tools.',
  iconSrc: '/images/icons/new-homes.avif',
  href: '/workspace',
};

export default JOURNEYS;
